import {
    BookOpen,
    Bug,
    Building2,
    Calculator,
    CalendarDays,
    ChartColumn,
    CheckCircle2,
    CircleDollarSign,
    FileCheck2,
    FolderOpen,
    GanttChartSquare,
    Headset,
    KeyRound,
    type LucideIcon,
    MonitorSmartphone,
    Settings,
    ShieldCheck,
    ShoppingCart,
    UserRound,
    Users,
    Wrench,
} from 'lucide-react';

export type PermissionCategory = 'all' | 'core' | 'projects' | 'service' | 'admin' | 'risky';

export type PermissionModule = {
    id: string;
    label: string;
    description: string;
    permissions: string[];
};

export const CRUD_ORDER = ['read', 'create', 'update', 'delete'] as const;

/**
 * A slug's "module" is its first segment, except for 3+ segment slugs
 * (e.g. `costs.projects.view`, `projects.files.read`) which group by the
 * first two segments so the entity/sub-area stays visible as its own card.
 */
export function moduleIdForPermission(permission: string): string {
    if (!permission.includes('.')) {
        return 'special';
    }

    const parts = permission.split('.');

    if (parts.length >= 3) {
        return `${parts[0]}.${parts[1]}`;
    }

    return parts[0];
}

export const ROLE_DESCRIPTIONS: Record<string, string> = {
    Administrator: 'Unrestricted access to all system functions',
    Engineer: 'Project delivery and technical execution',
    Manufacturing: 'Production project and task access',
    'Sales Manager': 'Full sales pipeline — leads, companies',
    'Purchase Manager': 'Procurement and purchase order management',
    'Service Manager': 'Work order and service operations leadership',
    'Service Supervisor': 'Field supervision and work order management',
    'Service Worker': 'Field work — own work orders and service requests',
    'Project Manager': 'Project planning, delivery, and team coordination',
    'Finance Manager': 'Financial oversight, costs, and analytics',
    'Operations Manager': 'Cross-department read-only oversight',
};

export function getRoleDescription(role: string): string {
    return (
        ROLE_DESCRIPTIONS[role] ??
        (role.includes('Administrator')
            ? 'Full system access'
            : role.includes('Finance')
              ? 'Financial management and reporting'
              : role.includes('Operations')
                ? 'Operations oversight'
                : role.includes('Service')
                  ? 'Service operations and workflow'
                  : role.includes('Project')
                    ? 'Project and execution management'
                    : role.includes('Sales')
                      ? 'Sales and pipeline management'
                      : role.includes('Engineer')
                        ? 'Technical project delivery'
                        : role.includes('Purchase')
                          ? 'Purchasing and procurement operations'
                          : 'Access profile role')
    );
}

const ENTITY_LABELS: Record<string, string> = {
    leads: 'leads',
    employees: 'employees',
    projects: 'projects',
    'work-orders': 'work orders',
    contacts: 'contacts',
    companies: 'companies',
    tasks: 'tasks',
    'bug-reports': 'bug reports',
    'knowledge-base': 'knowledge base articles',
    'service-requests': 'service requests',
    'calculation-requests': 'calculation requests',
    'purchase-orders': 'purchase orders',
    mpp: 'MPP file',
    adoc: 'ADOC document',
    dashboards: 'custom dashboards',
};

const SPECIAL_PERMISSION_DESCRIPTIONS: Record<string, string> = {
    'assign-employees': 'Assign internal users to companies and projects',
    'assign-contacts': 'Assign people to companies and users',
    'view-analysis': 'Access the main dashboard and analysis views',
    'analytics.view-financial': 'View financial analytics and revenue summaries',
    'analytics.boards.overview': 'View the Overview analytics board',
    'analytics.boards.companies': 'View the Companies analytics board',
    'analytics.boards.contacts': 'View the Contacts analytics board',
    'analytics.boards.projects': 'View the Projects analytics board',
    'analytics.boards.work-orders': 'View the Work Orders analytics board',
    'analytics.boards.team': 'View the Team analytics board',
    'analytics.boards.share': 'Give a specific person access to a pre-defined analytics board (grants them the permission directly)',
    'projects.export': 'Export projects to Excel or Gantt PDF',
    'projects.status.change': 'Change a project’s status (e.g. mark it completed, on hold or cancelled)',
    'projects.manufcaturing_number': 'Update the manufacturing number field on a project',
    'projects.manufacturing-number': 'Update the manufacturing number field on a project',
    'crm.export-sensitive': 'Export CRM data including GDPR-sensitive fields',
    'acl.view': 'View the roles and permissions panel for a user',
    'acl.manage': 'Edit role and permission assignments for any user',
    'settings.manage': 'Access and change global system settings',
    'settings.branding': 'Edit branding settings (logo, colours)',
    'settings.integrations': 'Configure external integrations',
    'settings.security': 'Manage security settings',
    'settings.sharepoint.manage': 'Configure the SharePoint integration',
    'projects.files.read': 'View and sync project SharePoint files',
    'projects.files.upload': "Upload files to a project's SharePoint folder",
    'projects.files.delete': 'Delete project files from SharePoint',
    'manage-company-cleanup': 'Access the data-import company cleanup tools',
    'merge-companies': 'Merge duplicate company records',
    'approve-registry-match': 'Approve company registry match suggestions',
    'manage-sales-approvals': 'Approve or reject offer documents on leads',
    'costs.projects.view': 'View cost lines on projects',
    'costs.projects.manage': 'Add, edit, and delete project cost lines',
    'costs.leads.view': 'View cost lines on leads',
    'costs.leads.manage': 'Add, edit, and delete lead cost lines',
    'leads.see-all': 'See every lead; without it a user sees only the leads they own or created themselves',
    'work-orders.see-all': 'See all work orders regardless of assignment',
    'work-orders.complete-work-order': 'Mark a work order as complete',
    'work-orders.can-cancel-work-order': 'Cancel a work order',
    'work-orders.update-status': 'Update the status field on a work order',
    'work-orders.update-priority': 'Update the priority field on a work order',
    'work-orders.update-type': 'Update the type field on a work order',
    'work-orders.assign': 'Assign or unassign workers on a work order',
    'work-orders.update-photos': 'Upload or delete work order photos',
    'work-orders.update-cost-lines': 'Add, edit, and delete work order cost lines',
    'work-orders.update-details': 'Edit core detail fields on a work order',
    'employees.suspend': 'Bulk-suspend employee accounts',
    'employees.impersonate': 'Log in as another user (impersonate)',
    'employees.manage-roles': 'Assign and remove roles from employees',
    'tasks.revert': 'Revert a task to a prior snapshot',
    'bug-reports.assign': 'Assign bug reports to a user for resolution',
    'bug-reports.export': 'Export bug report data',
    'knowledge-base.manage': 'Manage knowledge base categories and settings',
    'service-requests.see-all': 'See all service requests regardless of submitter',
    'service-requests.assign': 'Assign a service request to a user',
    'service-requests.manage-templates': 'Create, edit, and delete service request templates',
    'service-requests.export': 'Export service request data',
    'calculation-requests.see-all':
        'See every calculation request; without it a user sees only the ones they submitted, are assigned, or were added to as a contributor',
    'calculation-requests.assign': 'Assign or reassign the engineer, and manage contributors',
    'calculation-requests.start': 'Start calculating a queued request — engineering decides when work begins',
    'calculation-requests.complete': 'Record the calculated amount and close the calculation out',
    'calculation-requests.link-project': 'Link a calculated request to the project that was sold from it',
    'calculation-requests.export': 'Export calculation request data',
    'change-permissions': 'Change user permissions directly (system use)',
    'manage-system-settings': 'Manage system settings (system use)',
    'see-all-work-orders': 'See all work orders (legacy alias for work-orders.see-all)',
    'vacations.see-all': 'View every vacation request and approve/reject any of them (admin override)',
    'vacations.view-all': "Read-only access to the full vacation request log (all employees' requests and statuses)",
    'vacations.export': 'Export vacation records to Excel for accounting/HR',
    'vacations.manage': 'Configure vacation settings: companies, CEOs, org chart, leave types, holidays',
    'remote-devices.view': 'View enrolled devices and launch RustDesk remote sessions',
};

export function getPermissionDescription(permission: string): string {
    if (SPECIAL_PERMISSION_DESCRIPTIONS[permission]) {
        return SPECIAL_PERMISSION_DESCRIPTIONS[permission];
    }

    const parts = permission.split('.');
    const action = parts.at(-1);
    const entity = parts[0];
    const entityLabel = ENTITY_LABELS[entity] ?? entity.replaceAll('-', ' ');

    if (action === 'read') return `View ${entityLabel} records`;
    if (action === 'create') return `Create new ${entityLabel} records`;
    if (action === 'update') return `Edit and update ${entityLabel} records`;
    if (action === 'delete') return `Permanently delete ${entityLabel} records`;

    return '';
}

export const MODULE_LABELS: Record<string, string> = {
    companies: 'Companies',
    contacts: 'Contacts',
    leads: 'Leads',
    projects: 'Projects',
    'projects.files': 'Project Files',
    tasks: 'Tasks',
    'work-orders': 'Work Orders',
    employees: 'Employees',
    'costs.projects': 'Costs · Projects',
    'costs.leads': 'Costs · Leads',
    acl: 'Access Control',
    settings: 'Settings',
    analytics: 'Analytics',
    dashboards: 'Dashboards',
    crm: 'CRM Data',
    'knowledge-base': 'Knowledge Base',
    'service-requests': 'Service Requests',
    'calculation-requests': 'Calculation Requests',
    'purchase-orders': 'Purchase Orders',
    'bug-reports': 'Bug Reports',
    vacations: 'Vacations',
    'remote-devices': 'Remote Devices',
    mpp: 'MPP Viewer',
    adoc: 'ADOC Documents',
    special: 'Advanced Permissions',
};

export const MODULE_DESCRIPTIONS: Record<string, string> = {
    companies: 'Organization and company records',
    contacts: 'People and contact management',
    leads: 'Lead qualification and pipeline intake',
    projects: 'Project planning and execution',
    'projects.files': 'Project file access and uploads',
    tasks: 'Task and activity management',
    'work-orders': 'Service job access and workflow actions',
    employees: 'Employee profiles and user management',
    'costs.projects': 'View and manage project cost lines',
    'costs.leads': 'View and manage lead cost lines',
    acl: 'Role and permission administration',
    settings: 'System configuration and integrations',
    analytics: 'Reports and financial analytics',
    dashboards: 'Personal custom analytics dashboards',
    crm: 'Cross-CRM data and sensitive exports',
    'knowledge-base': 'Knowledge base articles',
    'service-requests': 'Service request intake and queue',
    'calculation-requests': 'Calculation request queue — what engineering priced and what was sold',
    'purchase-orders': 'Purchase order access',
    'bug-reports': 'Bug report triage and management',
    vacations: 'Vacation calendar — approvals, exports, and configuration',
    'remote-devices': 'IT remote-access console for enrolled devices',
    mpp: 'Microsoft Project (.mpp) file viewing',
    adoc: 'Signed ADOC e-document containers — open and download their contents',
    special: 'Cross-module and elevated permissions',
};

export const MODULE_ICONS: Record<string, LucideIcon> = {
    companies: Building2,
    contacts: Users,
    leads: UserRound,
    projects: FolderOpen,
    'projects.files': FolderOpen,
    tasks: CheckCircle2,
    'work-orders': Wrench,
    employees: ShieldCheck,
    'costs.projects': CircleDollarSign,
    'costs.leads': CircleDollarSign,
    acl: KeyRound,
    settings: Settings,
    analytics: ChartColumn,
    dashboards: ChartColumn,
    crm: Building2,
    'knowledge-base': BookOpen,
    'service-requests': Headset,
    'calculation-requests': Calculator,
    'purchase-orders': ShoppingCart,
    'bug-reports': Bug,
    vacations: CalendarDays,
    'remote-devices': MonitorSmartphone,
    mpp: GanttChartSquare,
    adoc: FileCheck2,
    special: ShieldCheck,
};

export const RISKY_PERMISSIONS = new Set<string>([
    'acl.manage',
    'settings.manage',
    'manage-sales-approvals',
    'assign-employees',
    'assign-contacts',
    'work-orders.can-cancel-work-order',
    'employees.impersonate',
    'employees.manage-roles',
    'crm.export-sensitive',
]);

export const FILTERS: Array<{ id: PermissionCategory; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'core', label: 'Core CRM' },
    { id: 'projects', label: 'Projects' },
    { id: 'service', label: 'Service' },
    { id: 'admin', label: 'Admin' },
    { id: 'risky', label: 'Risky only' },
];

export function prettyActionLabel(permission: string): string {
    const parts = permission.split('.');
    const action = parts.at(-1) ?? permission;

    if (action === 'read') return 'View';
    if (action === 'create') return 'Create';
    if (action === 'update') return 'Edit';
    if (action === 'delete') return 'Delete';

    return action
        .replaceAll('-', ' ')
        .split(' ')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

const ALWAYS_ADMIN = new Set([
    'acl.view',
    'acl.manage',
    'employees.suspend',
    'employees.impersonate',
    'employees.manage-roles',
    'change-permissions',
    'manage-system-settings',
]);

export function getPermissionCategory(permission: string): PermissionCategory {
    if (ALWAYS_ADMIN.has(permission)) return 'admin';
    if (
        permission.startsWith('manage-') ||
        permission.startsWith('settings.') ||
        permission.startsWith('acl.') ||
        permission.startsWith('remote-devices.')
    )
        return 'admin';

    if (
        permission.startsWith('costs.leads') ||
        permission.startsWith('companies.') ||
        permission.startsWith('contacts.') ||
        permission.startsWith('leads.') ||
        permission.startsWith('employees.') ||
        permission.startsWith('purchase-orders.')
    ) {
        return 'core';
    }

    if (
        permission.startsWith('costs.projects') ||
        permission.startsWith('projects.') ||
        permission.startsWith('tasks.') ||
        permission.startsWith('mpp.') ||
        permission.startsWith('adoc.')
    )
        return 'projects';

    if (
        permission.startsWith('work-orders.') ||
        permission.startsWith('service-requests.') ||
        permission === 'assign-employees' ||
        permission === 'assign-contacts'
    ) {
        return 'service';
    }

    return 'all';
}

export function buildModules(permissions: string[]): PermissionModule[] {
    const grouped = new Map<string, string[]>();

    permissions.forEach((permission) => {
        const moduleId = moduleIdForPermission(permission);
        const current = grouped.get(moduleId) ?? [];
        current.push(permission);
        grouped.set(moduleId, current);
    });

    return Array.from(grouped.entries())
        .map(([id, modulePermissions]) => {
            const sorted = [...modulePermissions].sort((a, b) => {
                const actionA = a.split('.').at(-1) ?? a;
                const actionB = b.split('.').at(-1) ?? b;
                const indexA = CRUD_ORDER.indexOf(actionA as (typeof CRUD_ORDER)[number]);
                const indexB = CRUD_ORDER.indexOf(actionB as (typeof CRUD_ORDER)[number]);

                if (indexA >= 0 && indexB >= 0) return indexA - indexB;
                if (indexA >= 0) return -1;
                if (indexB >= 0) return 1;
                return actionA.localeCompare(actionB);
            });

            return {
                id,
                label: MODULE_LABELS[id] ?? id,
                description: MODULE_DESCRIPTIONS[id] ?? 'Module permissions',
                permissions: sorted,
            };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
}
